import { useCallback, useEffect, useMemo, useState } from "react";
import { getAdsAuditLogs } from "../../../api/ads";
import type { AdsAuditLog, AdsAuditLogListParams } from "../../../api/ads";

export const useAdsAuditLogs = (initialParams: AdsAuditLogListParams = {}) => {
  const defaultParams: AdsAuditLogListParams = useMemo(
    () => ({
      pageNum: 1,
      pageSize: 10,
      search: "",
      ...initialParams,
    }),
    [initialParams]
  );

  const [params, setParamsState] = useState<AdsAuditLogListParams>(defaultParams);
  const [logs, setLogs] = useState<AdsAuditLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [listLoading, setListLoading] = useState(false);

  const fetchLogs = useCallback(
    async (override?: Partial<AdsAuditLogListParams>) => {
      try {
        setListLoading(true);
        setError(null);
        const merged: AdsAuditLogListParams = { ...params, ...override };
        const response = await getAdsAuditLogs(merged);
        setLogs(response.results);
        setTotalCount(response.totalCount);
        setTotalPages(response.totalPages);
      } catch (err: any) {
        setError(err?.message || "Failed to load audit logs");
      } finally {
        setListLoading(false);
      }
    },
    [params]
  );

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const setParams = useCallback((next: Partial<AdsAuditLogListParams>) => {
    setParamsState((prev) => ({ ...prev, ...next, pageNum: 1 }));
  }, []);

  const setPageNum = useCallback((pageNum: number) => {
    setParamsState((prev) => ({ ...prev, pageNum }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setParamsState((prev) => ({ ...prev, pageSize, pageNum: 1 }));
  }, []);

  return {
    functions: { fetchLogs, setParams, setPageNum, setPageSize },
    values: { logs, params, totalCount, totalPages, error },
    loading: { listLoading, actionLoading: false },
  };
};

